<template>
  <transition name="fullscreen-search">
    <div class="fullscreen-search" v-if="open">
      <div class="container">
        <div class="top">
          <input
            ref="input"
            autofocus
            :placeholder="$translations.search.placeholder"
            type="search"
            v-model="query"
            class="input"
          />
          <button class="close" @click="setOpen(false)">
            <i class="fal fa-times"></i>
          </button>
        </div>

        <transition name="loader-container">
          <div class="loader-container" v-if="waitGroup > 0">
            <div class="loader">Loading...</div>
          </div>
        </transition>

        <div class="results" v-if="dirty">
          <div class="articles">
            <!-- puorlife -->
            <div class="articles__column">
              <div class="title-container">
                <h4 class="title">{{ $translations.search.blog }}</h4>
                <a class="all t-link" href="/blogs/news">{{
                  $translations.search.see_all
                }}</a>
              </div>
              <div class="articles__results">
                <smooth-reflow>
                  <transition name="no-match">
                    <p class="no-match" v-if="articles.length === 0">
                      {{ $translations.search.no_articles }}
                    </p>
                  </transition>
                  <ul class="articles__list">
                    <li
                      class="articles__list__item"
                      v-for="article in articles"
                      :key="article.id"
                    >
                      <a :href="article.url">{{ article.title }}</a>
                    </li>
                  </ul>
                </smooth-reflow>
              </div>
            </div>

            <!-- ambassadors -->
            <div class="articles__column">
              <div class="title-container">
                <h4 class="title">{{ $translations.search.ambassadors }}</h4>
                <a class="all t-link" href="/blogs/news">{{
                  $translations.search.see_all
                }}</a>
              </div>
              <div class="articles__results">
                <smooth-reflow>
                  <transition name="no-match">
                    <p class="no-match" v-if="ambassadors.length === 0">
                      {{ $translations.search.no_ambassadors }}
                    </p>
                  </transition>
                  <ul class="articles__list">
                    <li
                      class="articles__list__item"
                      v-for="ambassador in ambassadors"
                      :key="ambassador.id"
                    >
                      <a :href="ambassador.url">{{ ambassador.title }}</a>
                    </li>
                  </ul>
                </smooth-reflow>
              </div>
            </div>
          </div>

          <div class="products">
            <div class="title-container">
              <h4 class="title">{{ $translations.search.products }}</h4>
              <a class="all t-link" href="/collections/all">{{
                $translations.search.see_all
              }}</a>
            </div>

            <transition name="no-match">
              <p class="no-match" v-if="products.length === 0">
                {{ $translations.search.no_products }}
              </p>
            </transition>

            <div class="grid" v-html="products"></div>
          </div>
        </div>
      </div></div
  ></transition>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import store from "../store";
import SmoothReflow from "./smooth-reflow.vue";
import { removeURLParameter } from "../util";

export default {
  name: "FullcreenSearch",
  selector: "#vue-fullscreen-search",
  components: { SmoothReflow },
  store,
  data() {
    return {
      dirty: false,
      waitGroup: 0,
    };
  },
  computed: {
    ...mapGetters({
      open: "search/open",
      articles: "search/articles",
      ambassadors: "search/ambassadors",
      products: "search/products",
    }),

    query: {
      get() {
        return this.$store.state.search.query;
      },
      set(value) {
        this.$store.commit("search/query", value);
      },
    },
  },
  methods: {
    ...mapMutations({
      setOpen: "search/open",
    }),
    ...mapActions({
      search: "search/search",
    }),
  },
  watch: {
    products() {
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('app:remount'));
      }, 1)
    },
    async query() {
      if (this.query.length === 0) {
        this.dirty = false;
      }
      if (this.query.length < 2) return;
      await this.search();
      this.dirty = true;
    },
    open() {
      if (this.open) {
        this.$nextTick(() => this.$refs.input.focus());
      }
    },
  },
  created() {
    if (window.location.search.includes("ps=1")) {
      this.setOpen(true);
      const url = window.location.toString();
      const newUrl = removeURLParameter(url, "ps");
      window.history.replaceState(null, "", newUrl);
    }

    const triggers = document.querySelectorAll('[data-search-trigger]');
    triggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        this.setOpen(true);
      })
    })
  },
};
</script>

<style lang="scss" scoped>
::v-deep .search-body-lock {
  overflow: hidden;
}

.fullscreen-search {
  position: fixed;
  z-index: z-index(fullsceen-search);
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: $white;
  overflow-y: scroll;
  padding-bottom: 80px;

  &-enter-active,
  &-leave-active {
    transition: opacity 0.2s ease-in-out;
  }

  &-enter,
  &-leave-to {
    opacity: 0;
  }
}

.container {
  @include inner-container();
  position: relative;
}

.loader-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  &-enter-active,
  &-leave-active {
    transition: opacity 0.2s ease-in-out;
  }

  &-enter,
  &-leave-to {
    opacity: 0;
  }
}

.loader,
.loader:before,
.loader:after {
  border-radius: 50%;
  width: 2.5em;
  height: 2.5em;
  animation-fill-mode: both;
  animation: loader 1.8s infinite ease-in-out;
}
.loader {
  color: $black;
  font-size: 10px;
  margin: 80px auto;
  position: relative;
  text-indent: -9999em;
  transform: translateZ(0);
  animation-delay: -0.16s;
}
.loader:before,
.loader:after {
  content: "";
  position: absolute;
  top: 0;
}
.loader:before {
  left: -3.5em;
  animation-delay: -0.32s;
}
.loader:after {
  left: 3.5em;
}

@keyframes loader {
  0%,
  80%,
  100% {
    box-shadow: 0 2.5em 0 -1.3em;
  }
  40% {
    box-shadow: 0 2.5em 0 0;
  }
}

.top {
  margin-top: 40px;
  display: flex;
  align-items: center;
}

.input {
  flex: 1;
  appearance: none;
  border: 0;
  font-family: $font-family;
  font-size: 21px;
  letter-spacing: 0;
  color: $black;
  outline: none;

  &::placeholder {
    color: rgba($black, 0.2);
  }
}

.close {
  @include button-reset();
  font-size: 21px;
}

.results {
  margin-top: 80px;
}

.articles {
  display: flex;

  @include media("<=tablet") {
    display: block;

    > * + * {
      margin-top: 32px;
    }
  }

  &__column {
    flex: 1;
  }

  &__results {
    margin-top: 16px;
  }

  &__list {
    margin: 0;
    padding: 0;
    list-style: none;

    &__item {
      a {
        color: $black;
        text-decoration: none;
        &:hover {
          color: $teal;
        }
      }

      & + & {
        margin-top: 8px;
      }
    }
  }
}

.products {
  margin-top: 56px;
}

.no-match {
  margin: 0;
  opacity: 0.5;
  &-enter-active,
  &-leave-active {
    transition: opacity 0.2s ease-in-out;
  }

  &-enter,
  &-leave-to {
    opacity: 0;
    position: absolute;
  }
}

.title-container {
  display: flex;
  align-items: center;
}

.title {
  font-size: 18px;
  margin: 0;
}

.all {
  font-size: 12px;
  margin-left: 24px;
}

.grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: auto;
  grid-row-gap: 48px;
  grid-column-gap: 48px;
  margin-top: 32px;

  @include media("<=tablet") {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
    grid-template-rows: auto;
    grid-column-gap: 24px;
  }

  @include media("<=tablet") {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
    grid-template-rows: auto;
    grid-column-gap: 16px;
  }

  &-item {
    display: inline-block;
    margin-right: 10px;
  }
  &-enter-active,
  &-leave-active {
    transition: all 1s;
  }
  &-enter,
  &-leave-to {
    opacity: 0;
    transform: translateY(30px);
  }
}
</style>
